import { AnyObject, yup } from '@framework/validation';
import { isEqual } from 'lodash';
import memoizeOne from 'memoize-one';

/**
 * Funzione memoized che ritorna un oggetto parziale soltanto alla modifica della source.
 * Utilizzato nei form (eg delle tab) quando si lavora su parti diverse di una stessa struttura dati
 * che rispondono alle stesse API (e.g. tipi-domanda).
 *
 * È sufficiente creare uno schema parziale (e.g. BaseSchema.pick([...])/.omit([...])) e passarlo alla funzione.
 * utilizzare la funzione risultante in un useMemo con i value ritornati dalla get "globale". In questo modo,
 * anche in caso di invalidate sulla GET gli initialValues del form si resettano solo quando effettivamente
 * vengono modificati i valori che interessano.
 */
export function getPartialInitialValues<T extends AnyObject>(
  partialSchema: yup.ObjectSchema<T>,
) {
  return memoizeOne(
    (values: T) => {
      return partialSchema.cast(values, { assert: false });
    },
    ([prevValues], [nextValues]) => {
      const prevCasted = partialSchema.cast(prevValues, {
        assert: false,
      });
      const nextCasted = partialSchema.cast(nextValues, {
        assert: false,
      });
      return isEqual(prevCasted, nextCasted);
    },
  );
}
